import React, { useState, useMemo, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Box,
  Checkbox,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function SortableTableHead({
  columns,
  order,
  orderBy,
  onRequestSort,
  enableSelection,
  onSelectAllClick,
  numSelected,
  rowCount
}) {
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {enableSelection && (
          <TableCell padding="checkbox">
            <Checkbox
              color="primary"
              indeterminate={numSelected > 0 && numSelected < rowCount}
              checked={rowCount > 0 && numSelected === rowCount}
              onChange={onSelectAllClick}
            />
          </TableCell>
        )}
        {columns.map((column) => (
          <TableCell
            key={column.id}
            align={column.align || 'left'}
            padding={column.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === column.id ? order : false}
            sx={column.sx}
          >
            {column.sortable !== false ? (
              <TableSortLabel
                active={orderBy === column.id}
                direction={orderBy === column.id ? order : 'asc'}
                onClick={createSortHandler(column.id)}
              >
                {column.label}
                {orderBy === column.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            ) : (
              column.label
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

function SortableTable({
  data = [],
  columns = [],
  defaultOrderBy = null,
  defaultOrder = 'asc',
  renderRow,
  containerProps = {},
  tableProps = {},
  enableSelection = false,
  onSelectionChange,
  selectionKey = 'id',
  order: orderProp,
  orderBy: orderByProp,
  onSortChange,
}) {
  const [internalOrder, setInternalOrder] = useState(defaultOrder);
  const [internalOrderBy, setInternalOrderBy] = useState(defaultOrderBy);
  const [selectedRows, setSelectedRows] = useState(new Set());

  const isControlled = orderProp !== undefined && orderByProp !== undefined;
  const order = isControlled ? orderProp : internalOrder;
  const orderBy = isControlled ? orderByProp : internalOrderBy;

  // Notify parent component when selection changes
  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(Array.from(selectedRows));
    }
  }, [selectedRows, onSelectionChange]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    
    if (onSortChange) {
      onSortChange(property, newOrder);
    }
    
    if (!isControlled) {
      setInternalOrder(newOrder);
      setInternalOrderBy(property);
    }
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelectedRows = new Set(data.map((row) => row[selectionKey]));
      setSelectedRows(newSelectedRows);
      return;
    }
    setSelectedRows(new Set());
  };

  const handleSelectRow = (id) => {
    const newSelectedRows = new Set(selectedRows);
    if (newSelectedRows.has(id)) {
      newSelectedRows.delete(id);
    } else {
      newSelectedRows.add(id);
    }
    setSelectedRows(newSelectedRows);
  };

  const sortedData = useMemo(() => {
    if (isControlled) {
      return data;
    }
    if (!orderBy) {
      return data;
    }
    return stableSort(data, getComparator(order, orderBy));
  }, [data, order, orderBy, isControlled]);

  // Create a wrapper for renderRow to include selection checkbox
  const renderRowWithSelection = (row, index) => {
    if (!enableSelection) {
      return renderRow(row, index);
    }

    const isItemSelected = selectedRows.has(row[selectionKey]);

    // Create a new renderRow function that includes the selection checkbox
    const rowWithSelection = (
      <TableRow
        key={row[selectionKey]}
        selected={isItemSelected}
      >
        <TableCell padding="checkbox">
          <Checkbox
            color="primary"
            checked={isItemSelected}
            onChange={() => handleSelectRow(row[selectionKey])}
          />
        </TableCell>
        {renderRow(row, index).props.children}
      </TableRow>
    );

    return rowWithSelection;
  };

  return (
    <TableContainer component={Paper} {...containerProps}>
      <Table {...tableProps}>
        <SortableTableHead
          columns={columns}
          order={order}
          orderBy={orderBy}
          onRequestSort={handleRequestSort}
          enableSelection={enableSelection}
          onSelectAllClick={handleSelectAllClick}
          numSelected={selectedRows.size}
          rowCount={data.length}
        />
        <TableBody>
          {sortedData.map((row, index) => {
            const element = renderRowWithSelection(row, index);
            // If selection is disabled and the element returned is not a TableRow
            // (i.e., it's individual cells), we need to wrap it in a TableRow
            if (!enableSelection && element && element.props && element.props.children && element.type !== TableRow) {
              return (
                <TableRow key={row[selectionKey] || index}>
                  {element}
                </TableRow>
              );
            }
            return element;
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default SortableTable;